import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { BookingPageProvider } from "./BookingPageContext";
import meetingTypeService, { MeetingType } from "../../services/meetingTypeService";
import { BookingActionService, BookingActionContext } from "../../services/bookingActionService";
import { ThemeProvider } from "../../theme/ThemeProvider";
import { BookingDataProvider, useBookingData } from "../../providers/BookingDataProvider";
import { BookingDebugProvider } from "../../contexts/BookingDebugContext";
import BookingPageContent, { BookingPageContentProps } from "./components/BookingPageContent";
import BookingErrorPage from "./components/BookingErrorPage";
import { trackBookingPageViewed } from "../../services/analyticsTrackingService";

/**
 * BookingPageContainer Component
 *
 * This component serves as the main entry point for the booking page.
 * It prefetches data and sets up the necessary providers.
 * 
 * Supports three modes:
 * - Default: Normal booking flow with username/slug
 * - Reschedule: Token-based reschedule flow
 * - Cancel: Token-based cancel flow
 */

/**
 * Loading spinner for the booking page.
 * A centered brand-colored spinner replaces the skeleton for better perceived performance
 * during multi-second waits (spinners feel faster than skeleton placeholders).
 */
const BookingPageLoadingSpinner: React.FC = () => (
  <div className="max-w-6xl mx-auto px-4 py-8">
    <div className="flex items-center justify-center min-h-[400px]">
      <div className="flex flex-col items-center">
        <svg
          className="h-10 w-10 animate-spin mb-3"
          style={{ color: 'var(--brand-color, #2E7AA0)' }}
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
      </div>
    </div>
  </div>
);

/**
 * Inner gate component that has access to BookingDataProvider context.
 * Shows a spinner while the meeting type is loading, then renders content immediately.
 * Calendar data loads progressively within BookingFlow (its own built-in spinner).
 * This gives first meaningful paint at ~2s instead of waiting for all data (~15s).
 */
const BookingContentGate: React.FC<{
  meetingTypeLoading: boolean;
  prefetchedMeetingType: MeetingType | null;
} & Omit<BookingPageContentProps, 'prefetchedMeetingType'>> = ({
  meetingTypeLoading,
  prefetchedMeetingType,
  ...contentProps
}) => {
  const { setMeetingType: dataProviderSetMeetingType } = useBookingData();
  const [meetingTypeSetOnProvider, setMeetingTypeSetOnProvider] = useState(false);

  // Set meeting type on data provider EARLY (while spinner is still visible).
  // This triggers auto-prefetch of calendar slots inside BookingDataProvider.
  useEffect(() => {
    if (prefetchedMeetingType && prefetchedMeetingType.id && !meetingTypeSetOnProvider) {
      dataProviderSetMeetingType(prefetchedMeetingType);
      setMeetingTypeSetOnProvider(true);
    }
  }, [prefetchedMeetingType, meetingTypeSetOnProvider, dataProviderSetMeetingType]);

  if (meetingTypeLoading) {
    return <BookingPageLoadingSpinner />;
  }

  return <BookingPageContent prefetchedMeetingType={prefetchedMeetingType} {...contentProps} />;
};

interface BookingPageContainerProps {
  mode?: 'booking' | 'reschedule' | 'cancel';
}

const BookingPageContainer: React.FC<BookingPageContainerProps> = ({ mode }) => {
  // Extract parameters from URL based on mode
  const { username, slug, token } = useParams<{ 
    username?: string; 
    slug?: string; 
    token?: string; 
  }>();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isError, setIsError] = useState<boolean>(false);
  const [errorType, setErrorType] = useState<'404' | 'error' | 'locked'>('error');
  /** Why it was locked - the host's plan, or our own release schedule. */
  const [lockedReason, setLockedReason] = useState<string | null>(null);
  const [bookingContext, setBookingContext] = useState<BookingActionContext | null>(null);
  const [prefetchedMeetingType, setPrefetchedMeetingType] = useState<MeetingType | null>(null);

  // Use auth variables directly from primaryAuth
  const { token: userAuthToken } = useAuth();

  // Prefetch data on component mount based on mode
  useEffect(() => {
    const abortController = new AbortController();
    
    const fetchData = async () => {
      // Action mode (reschedule/cancel) - fetch booking data by token
      if (mode && token) {
        try {
          setIsLoading(true);
          
          const booking = await BookingActionService.getBookingByToken(token);
          
          if (abortController.signal.aborted) {
            return;
          }
          
          setBookingContext(booking);
          setIsLoading(false);
        } catch (error: any) {
          if (!abortController.signal.aborted) {
            setIsError(true);
            /**
             * ⚠️ CLASSIFY THE FAILURE; DO NOT CARRY ITS TEXT.
             *
             * `BookingErrorPage` says everything in the reader's language from
             * the type alone. This used to pass `error.response.data.message ||
             * error.message` through to it, which put an English sentence
             * meant for a log — or an axios one, "Request failed with status
             * code 500" — in front of a guest on a Japanese booking page. The
             * detail is still in the network tab, where it is useful.
             */
            if (error.response?.status === 403 && error.response?.data?.isLocked) {
              setErrorType('locked');
              setLockedReason(error.response?.data?.lockedReason ?? null);
            } else if (error.response?.status === 404 || error.message?.includes('not found')) {
              setErrorType('404');
            } else {
              setErrorType('error');
            }
            setIsLoading(false);
          }
        }
        return;
      }
      
      // Normal booking mode - fetch meeting type by username/slug
      if (!username || !slug) {
        setIsError(true);
        setErrorType('404');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        
        // Single fetch of meeting type data + first-month availability
        // Pass browser timezone so server can inline first-month slots in the response
        const meetingType = await meetingTypeService.getMeetingTypeBySlug(username, slug);
        
        // Only continue if request wasn't aborted
        if (abortController.signal.aborted) {
          return;
        }

        if (meetingType && meetingType.id) {
          // Store prefetched meeting type to pass to BookingPageContent (avoids duplicate fetch)
          setPrefetchedMeetingType(meetingType);
          // Auto-prefetching of availability is handled by BookingDataProvider

          // Booking funnel step 1. Only for a real first visit to the booking
          // page — `mode` is undefined here for /:username/:slug and set for
          // reschedule/cancel, which are not new bookings. (Preview never
          // mounts this container.) Deduped per session inside the service.
          if (!mode) {
            trackBookingPageViewed(meetingType.id);
          }
        }
      } catch (error: any) {
        if (!abortController.signal.aborted) {
          setIsError(true);
          // Check if it's a 403 locked error (meeting type requires upgraded plan)
          if (error.response?.status === 403 && error.response?.data?.isLocked) {
            setErrorType('locked');
            setLockedReason(error.response?.data?.lockedReason ?? null);
          } else if (error.response?.status === 404 || error.message?.toLowerCase().includes('not found')) {
            setErrorType('404');
          } else {
            setErrorType('error');
          }
        }
      } finally {
        if (!abortController.signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    fetchData();

    // Cleanup function to abort requests if component unmounts
    return () => {
      abortController.abort();
    };
  }, [username, slug, mode, token]);

  // Keep the browser tab in step with the meeting being booked.
  //
  // The HTML served for /:username/:slug already carries the right <title>
  // (the web service injects it alongside the Open Graph tags, see
  // web/src/lib/metaTags.ts). This handles the case that injection cannot:
  // an in-app navigation between two booking pages, where no new document is
  // ever fetched and the title would otherwise stay on the previous meeting.
  //
  // Not translated on purpose — the value is the host's own meeting title.
  useEffect(() => {
    const meetingTitle = prefetchedMeetingType?.title;
    if (!meetingTitle) return;

    const previousTitle = document.title;
    document.title = meetingTitle;

    return () => {
      document.title = previousTitle;
    };
  }, [prefetchedMeetingType?.title]);

  if (isError) {
    return (
      <BookingErrorPage
        type={errorType}
        lockedReason={lockedReason}
      />
    );
  }

  return (
    <ThemeProvider>
      <BookingDataProvider initialToken={userAuthToken}>
        <BookingDebugProvider>
          <BookingPageProvider>
              <BookingContentGate
                meetingTypeLoading={isLoading}
                prefetchedMeetingType={prefetchedMeetingType}
                username={username}
                slug={slug}
                mode={mode}
                token={token}
                bookingContext={bookingContext}
              />
            </BookingPageProvider>
        </BookingDebugProvider>
      </BookingDataProvider>
    </ThemeProvider>
  );
};

export default BookingPageContainer;
